import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

type ContentType = {
  title: string;
  lastUpdated: string;
  sections: {
    title: string;
    content: string[];
  }[];
};

const KVKKPage: React.FC = () => {
  const { language } = useLanguage();
  const isTr = language === 'tr';

  const enContent: ContentType = {
    title: 'Personal Data Protection Policy (GDPR)',
    lastUpdated: 'Last Updated: December 29, 2025',
    sections: [
      {
        title: '1. Data Controller',
        content: [
          'Mr.Mirror® ("we", "us", or "our") is the data controller for the personal data we collect and process through our platform.',
          'We are committed to protecting your personal data and respecting your privacy rights in accordance with GDPR and other applicable data protection laws.',
        ],
      },
      {
        title: '2. Personal Data We Collect',
        content: [
          'We collect and process the following types of personal data:',
          '• Account Information: Username, email address, full name, password (encrypted)',
          '• Usage Data: Script analysis history, feature usage, preferences, settings',
          '• Technical Data: IP address, browser type, device information, cookies',
          '• Payment Information: Payment details are processed securely through third-party payment processors',
          '• Communication Data: Messages sent through our contact form, support requests',
        ],
      },
      {
        title: '3. Legal Basis for Processing',
        content: [
          'We process your personal data based on the following legal grounds:',
          '• Consent: When you register and use our services',
          '• Contract Performance: To provide the services you have subscribed to',
          '• Legitimate Interests: To improve our services, prevent fraud, and ensure security',
          '• Legal Obligations: To comply with applicable laws and regulations',
        ],
      },
      {
        title: '4. How We Use Your Data',
        content: [
          'We use your personal data for the following purposes:',
          '• Providing and maintaining our AI-powered script analysis services',
          '• Processing your scripts and generating analysis reports',
          '• Managing your account and subscriptions',
          '• Communicating with you about our services',
          '• Improving our platform and developing new features',
          '• Ensuring security and preventing fraud',
          '• Complying with legal obligations',
        ],
      },
      {
        title: '5. Data Sharing and Disclosure',
        content: [
          'We do not sell your personal data. We may share your data with:',
          '• Service Providers: Third-party companies that help us operate our platform (hosting, payment processing, analytics)',
          '• Legal Requirements: When required by law or to protect our rights',
          '• Business Transfers: In case of merger, acquisition, or sale of assets',
          'All third parties are required to protect your data in accordance with applicable laws.',
        ],
      },
      {
        title: '6. Your Rights',
        content: [
          'Under GDPR and other data protection laws, you have the following rights:',
          '• Right to Access: Request a copy of your personal data',
          '• Right to Rectification: Correct inaccurate or incomplete data',
          '• Right to Erasure: Request deletion of your personal data ("right to be forgotten")',
          '• Right to Restriction: Limit how we use your data',
          '• Right to Data Portability: Receive your data in a structured format',
          '• Right to Object: Object to certain types of processing',
          '• Right to Withdraw Consent: Withdraw your consent at any time',
          'To exercise these rights, please contact us at privacy@mrmirror.ai',
        ],
      },
      {
        title: '7. Data Security',
        content: [
          'We implement appropriate technical and organizational measures to protect your personal data:',
          '• Encryption of data in transit and at rest',
          '• Secure authentication and access controls',
          '• Regular security audits and updates',
          '• Employee training on data protection',
          '• Incident response and breach notification procedures',
        ],
      },
      {
        title: '8. Data Retention',
        content: [
          'We retain your personal data only for as long as necessary to:',
          '• Provide our services to you',
          '• Comply with legal obligations',
          '• Resolve disputes and enforce agreements',
          'When you delete your account, we will delete or anonymize your personal data within 30 days, unless we are required to retain it for legal purposes.',
        ],
      },
      {
        title: '9. International Data Transfers',
        content: [
          'Your personal data may be transferred to and processed in countries outside your country of residence.',
          'We ensure that such transfers comply with applicable data protection laws through:',
          '• Standard Contractual Clauses approved by the European Commission',
          '• Adequate protection mechanisms',
          '• Your explicit consent where required',
        ],
      },
      {
        title: '10. Cookies and Tracking',
        content: [
          'We use cookies and similar tracking technologies to:',
          '• Remember your preferences and settings',
          '• Analyze usage and improve our services',
          '• Provide personalized content',
          'You can control cookies through your browser settings. For more information, see our Cookie Policy.',
        ],
      },
      {
        title: '11. Children\'s Privacy',
        content: [
          'Our services are not intended for children under 16 years of age.',
          'We do not knowingly collect personal data from children.',
          'If we become aware that we have collected data from a child, we will delete it promptly.',
        ],
      },
      {
        title: '12. Changes to This Policy',
        content: [
          'We may update this policy from time to time to reflect changes in our practices or legal requirements.',
          'We will notify you of any material changes by email or through our platform.',
          'Your continued use of our services after such changes constitutes acceptance of the updated policy.',
        ],
      },
      {
        title: '13. Contact Information',
        content: [
          'For questions or concerns about this policy or our data practices, please contact:',
          'Email: privacy@mrmirror.ai',
          'Data Protection Officer: dpo@mrmirror.ai',
          'You also have the right to lodge a complaint with your local data protection authority.',
        ],
      },
    ],
  };

  const trContent: ContentType = {
    title: 'Kişisel Verilerin Korunması Politikası (KVKK)',
    lastUpdated: 'Son Güncelleme: 29 Aralık 2025',
    sections: [
      {
        title: '1. Veri Sorumlusu',
        content: [
          'Mr.Mirror® ("biz", "bize" veya "bizim") platformumuz üzerinden topladığımız ve işlediğimiz kişisel veriler için veri sorumlusudur.',
          '6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK), GDPR ve diğer geçerli veri koruma yasalarına uygun olarak kişisel verilerinizi korumaya ve gizlilik haklarınıza saygı göstermeye kararlıyız.',
        ],
      },
      {
        title: '2. Topladığımız Kişisel Veriler',
        content: [
          'Aşağıdaki türde kişisel verileri topluyoruz ve işliyoruz:',
          '• Hesap Bilgileri: Kullanıcı adı, e-posta adresi, ad soyad, şifre (şifrelenmiş)',
          '• Kullanım Verileri: Senaryo analiz geçmişi, özellik kullanımı, tercihler, ayarlar',
          '• Teknik Veriler: IP adresi, tarayıcı türü, cihaz bilgileri, çerezler',
          '• Ödeme Bilgileri: Ödeme detayları üçüncü taraf ödeme işlemcileri aracılığıyla güvenli bir şekilde işlenir',
          '• İletişim Verileri: İletişim formu üzerinden gönderilen mesajlar, destek talepleri',
        ],
      },
      {
        title: '3. İşleme Hukuki Dayanağı',
        content: [
          'Kişisel verilerinizi aşağıdaki hukuki temellere dayanarak işliyoruz:',
          '• Açık Rıza: Kayıt olduğunuzda ve hizmetlerimizi kullandığınızda',
          '• Sözleşmenin İfası: Abone olduğunuz hizmetleri sağlamak için',
          '• Meşru Menfaatler: Hizmetlerimizi geliştirmek, dolandırıcılığı önlemek ve güvenliği sağlamak için',
          '• Yasal Yükümlülükler: Geçerli yasa ve yönetmeliklere uymak için',
        ],
      },
      {
        title: '4. Verilerinizi Nasıl Kullanıyoruz',
        content: [
          'Kişisel verilerinizi aşağıdaki amaçlarla kullanıyoruz:',
          '• Yapay zeka destekli senaryo analiz hizmetlerimizi sağlamak ve sürdürmek',
          '• Senaryolarınızı işlemek ve analiz raporları oluşturmak',
          '• Hesabınızı ve aboneliklerinizi yönetmek',
          '• Hizmetlerimiz hakkında sizinle iletişim kurmak',
          '• Platformumuzu geliştirmek ve yeni özellikler geliştirmek',
          '• Güvenliği sağlamak ve dolandırıcılığı önlemek',
          '• Yasal yükümlülüklere uymak',
        ],
      },
      {
        title: '5. Veri Paylaşımı ve Açıklama',
        content: [
          'Kişisel verilerinizi satmıyoruz. Verilerinizi şu taraflarla paylaşabiliriz:',
          '• Hizmet Sağlayıcılar: Platformumuzu işletmemize yardımcı olan üçüncü taraf şirketler (hosting, ödeme işleme, analitik)',
          '• Yasal Gereklilikler: Kanun gereği veya haklarımızı korumak için gerektiğinde',
          '• İş Transferleri: Birleşme, satın alma veya varlık satışı durumunda',
          'Tüm üçüncü taraflar, verilerinizi geçerli yasalara uygun olarak korumakla yükümlüdür.',
        ],
      },
      {
        title: '6. Haklarınız',
        content: [
          'KVKK, GDPR ve diğer veri koruma yasaları kapsamında aşağıdaki haklara sahipsiniz:',
          '• Bilgi Talep Etme Hakkı: Kişisel verilerinizin bir kopyasını talep edebilirsiniz',
          '• Düzeltme Hakkı: Yanlış veya eksik verileri düzeltebilirsiniz',
          '• Silme Hakkı: Kişisel verilerinizin silinmesini talep edebilirsiniz ("unutulma hakkı")',
          '• İşlemenin Kısıtlanması Hakkı: Verilerinizi nasıl kullandığımızı sınırlayabilirsiniz',
          '• Veri Taşınabilirliği Hakkı: Verilerinizi yapılandırılmış bir formatta alabilirsiniz',
          '• İtiraz Etme Hakkı: Belirli işleme türlerine itiraz edebilirsiniz',
          '• Rızayı Geri Çekme Hakkı: Rızanızı istediğiniz zaman geri çekebilirsiniz',
          'Bu hakları kullanmak için lütfen privacy@mrmirror.ai adresinden bizimle iletişime geçin.',
        ],
      },
      {
        title: '7. Veri Güvenliği',
        content: [
          'Kişisel verilerinizi korumak için uygun teknik ve organizasyonel önlemler uyguluyoruz:',
          '• İletim ve depolama sırasında veri şifreleme',
          '• Güvenli kimlik doğrulama ve erişim kontrolleri',
          '• Düzenli güvenlik denetimleri ve güncellemeler',
          '• Çalışanların veri koruması konusunda eğitimi',
          '• Olay müdahalesi ve ihlal bildirim prosedürleri',
        ],
      },
      {
        title: '8. Veri Saklama',
        content: [
          'Kişisel verilerinizi yalnızca şu amaçlar için gerekli olduğu sürece saklarız:',
          '• Size hizmetlerimizi sağlamak',
          '• Yasal yükümlülüklere uymak',
          '• Anlaşmazlıkları çözmek ve anlaşmaları uygulamak',
          'Hesabınızı sildiğinizde, yasal amaçlarla saklamak zorunda olmadığımız sürece kişisel verilerinizi 30 gün içinde siler veya anonimleştiririz.',
        ],
      },
      {
        title: '9. Uluslararası Veri Transferleri',
        content: [
          'Kişisel verileriniz, ikamet ettiğiniz ülke dışındaki ülkelere aktarılabilir ve işlenebilir.',
          'Bu tür transferlerin geçerli veri koruma yasalarına uygun olduğundan aşağıdaki yollarla emin oluruz:',
          '• Avrupa Komisyonu tarafından onaylanan Standart Sözleşme Hükümleri',
          '• Yeterli koruma mekanizmaları',
          '• Gerektiğinde açık rızanız',
        ],
      },
      {
        title: '10. Çerezler ve İzleme',
        content: [
          'Çerezleri ve benzer izleme teknolojilerini şu amaçlarla kullanıyoruz:',
          '• Tercihlerinizi ve ayarlarınızı hatırlamak',
          '• Kullanımı analiz etmek ve hizmetlerimizi geliştirmek',
          '• Kişiselleştirilmiş içerik sağlamak',
          'Çerezleri tarayıcı ayarlarınız üzerinden kontrol edebilirsiniz. Daha fazla bilgi için Çerez Politikamıza bakın.',
        ],
      },
      {
        title: '11. Çocukların Gizliliği',
        content: [
          'Hizmetlerimiz 16 yaşın altındaki çocuklara yönelik değildir.',
          'Çocuklardan bilerek kişisel veri toplamıyoruz.',
          'Bir çocuktan veri topladığımızı öğrenirsek, bunu derhal sileriz.',
        ],
      },
      {
        title: '12. Bu Politikadaki Değişiklikler',
        content: [
          'Uygulamalarımızdaki veya yasal gerekliliklerdeki değişiklikleri yansıtmak için bu politikayı zaman zaman güncelleyebiliriz.',
          'Önemli değişiklikleri e-posta yoluyla veya platformumuz üzerinden size bildireceğiz.',
          'Bu tür değişikliklerden sonra hizmetlerimizi kullanmaya devam etmeniz, güncellenen politikayı kabul ettiğiniz anlamına gelir.',
        ],
      },
      {
        title: '13. İletişim Bilgileri',
        content: [
          'Bu politika veya veri uygulamalarımız hakkında sorularınız veya endişeleriniz için lütfen iletişime geçin:',
          'E-posta: privacy@mrmirror.ai',
          'Veri Koruma Görevlisi: dpo@mrmirror.ai',
          'Ayrıca yerel veri koruma otoritenize şikayette bulunma hakkınız vardır.',
        ],
      },
    ],
  };

  const content: Record<string, ContentType> = {
    en: enContent,
    tr: trContent,
    fr: enContent, // French users see English for now
    es: enContent, // Spanish users see English for now
    ar: enContent, // Arabic users see English for now
    de: enContent, // German users see English for now
    it: enContent, // Italian users see English for now
    ru: enContent, // Russian users see English for now
  };

  const kvkk: ContentType = content[language] || enContent;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />

      <main className="flex-1 max-w-4xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24 w-full">
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/03</span> · {isTr ? 'YASAL' : 'LEGAL'}</span>
          <span className="eyebrow-mute hidden md:inline">{kvkk.lastUpdated.toUpperCase()}</span>
        </div>

        <div className="mb-16 pb-12 border-b border-hairline">
          <h1 className="display-h display-lg mb-4">{kvkk.title}</h1>
          <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-mute">
            {kvkk.lastUpdated}
          </p>
        </div>

        <article className="space-y-14">
          {kvkk.sections.map((section, index) => (
            <section key={index} className="grid grid-cols-12 gap-6 md:gap-10">
              <div className="col-span-12 md:col-span-3">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent block mb-2">
                  /{String(index + 1).padStart(2, '0')}
                </span>
                <h2 className="font-serif italic text-xl md:text-2xl text-ink leading-tight">
                  {section.title.replace(/^\d+\.\s*/, '')}
                </h2>
              </div>
              <div className="col-span-12 md:col-span-9">
                <div className="space-y-3 font-prose text-[15px] leading-relaxed text-ink-dim">
                  {section.content.map((paragraph, pIndex) => (
                    <p key={pIndex} className="leading-relaxed">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </section>
          ))}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default KVKKPage;

